import { computed, inject, Injectable, Signal } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { RestCmsService } from '../../../rest/rest.cms.service';
import { CmsStore } from './cms.store';
import { AdditionalCustomFieldsContent, AGBArticle, CmsState } from './cms.types';

@Injectable({providedIn: 'root'})
export class CmsService {

  private cmsStore = inject(CmsStore);
  private restCmsService = inject(RestCmsService);
  private contentSubscription: Subscription;
  private agbSubscription: Subscription;
  private aboutSubscription: Subscription;
  private faqSubscription: Subscription;
  private faqTextSubscription: Subscription;
  private impressumTextSubscription: Subscription;
  private datenschutzTextSubscription: Subscription;
  private newsSubscription: Subscription;
  private statusSubscription: Subscription;

  public loadCmsContent() {
    if (this.contentSubscription) {
      this.contentSubscription.unsubscribe();
    }
    this.contentSubscription = this.restCmsService.getCmsContent()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          cmsContent: result
        });
      });
  }

  public getCmsContent(): Signal<AdditionalCustomFieldsContent> {
    return computed(() => this.cmsStore.currentCmsData()?.cmsContent);
  }

  public loadCmsAGB() {
    if (this.agbSubscription) {
      this.agbSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingAgb: true
    });
    this.agbSubscription = this.restCmsService.getCmsAGB()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          agb:        result,
          loadingAgb: false
        });
      });
  }

  public loadCmsAbout() {
    if (this.aboutSubscription) {
      this.aboutSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingCmsAbout: true
    });
    this.aboutSubscription = this.restCmsService.getCmsAbout()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          cmsAbout:        result,
          loadingCmsAbout: false
        });
      });
  }

  public loadCmsFAQText() {
    if (this.faqTextSubscription) {
      this.faqTextSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingCmsFaqText: true
    });
    this.faqTextSubscription = this.restCmsService.getCmsFAQText()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          cmsFaqText:        result,
          loadingCmsFaqText: false
        });
      });
  }

  public loadCmsFAQGroupArticles() {
    if (this.faqSubscription) {
      this.faqSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingFaqGroupArticle: true
    });
    this.faqSubscription = this.restCmsService.getCmsFAQ()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          faqGroupArticle:        result,
          loadingFaqGroupArticle: false
        });
      });
  }

  public loadCmsImpressum() {
    if (this.impressumTextSubscription) {
      this.impressumTextSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingCmsImprint: true
    });
    this.impressumTextSubscription = this.restCmsService.getCmsImpressum()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          cmsImprint:        result,
          loadingCmsImprint: false
        });
      });
  }

  public loadCmsDatenschutz() {
    if (this.datenschutzTextSubscription) {
      this.datenschutzTextSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingCmsPrivacy: true
    });
    this.datenschutzTextSubscription = this.restCmsService.getCmsDatenschutz()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          cmsPrivacy:        result,
          loadingCmsPrivacy: false
        });
      });
  }

  public loadCmsNews() {
    if (this.newsSubscription) {
      this.newsSubscription.unsubscribe();
    }
    this.cmsStore.updateCMSPartial({
      loadingNews: true
    });
    this.newsSubscription = this.restCmsService.getNews()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        if (result) {
          result.sort((a, b) => (a.menu_order > b.menu_order ? 1 : -1));
        }
        this.cmsStore.updateCMSPartial({
          news:        result,
          loadingNews: false
        });
      });
  }

  public loadCmsStatusmeldung() {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    this.newsSubscription = this.restCmsService.getStatusMeldung()
      .pipe(
        take(1)
      )
      .subscribe(result => {
        this.cmsStore.updateCMSPartial({
          statusMeldung: result
        });
      });
  }

  public getCMSState(): Signal<CmsState> {
    return computed(() => this.cmsStore.currentCmsData());
  }
}
